import {
  forwardRef,
  type InputHTMLAttributes,
  type LabelHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from '../lib/utils';

/** DESIGN.md §6 Form fields. */
export const fieldClass =
  'h-10 w-full rounded-lg border border-[var(--line)] bg-[var(--surface)] px-3 text-sm text-[var(--ink)] placeholder:text-[var(--ink-faint)] focus:border-[var(--line-strong)] focus:outline-none disabled:opacity-60 aria-[invalid=true]:border-[var(--negative)]';

export const labelClass = 'mb-1.5 block text-sm text-[var(--ink-soft)]';

export const checkboxClass = 'h-4 w-4 accent-[var(--accent)]';

export function Label({ className, ...props }: LabelHTMLAttributes<HTMLLabelElement>) {
  return <label className={cn(labelClass, className)} {...props} />;
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...props }, ref) {
    return <input ref={ref} className={cn(fieldClass, className)} {...props} />;
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, ...props }, ref) {
    return <select ref={ref} className={cn(fieldClass, 'pr-8', className)} {...props} />;
  },
);

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, ...props }, ref) {
  return (
    <textarea ref={ref} className={cn(fieldClass, 'h-auto min-h-20 py-2', className)} {...props} />
  );
});

export function FieldError({ id, children }: { id?: string; children?: ReactNode }) {
  if (!children) return null;
  return (
    <p id={id} className="mt-1.5 text-sm text-[var(--negative)]">
      {children}
    </p>
  );
}

export function FieldHint({ id, children }: { id?: string; children?: ReactNode }) {
  if (!children) return null;
  return (
    <p id={id} className="mt-1.5 text-xs text-[var(--ink-faint)]">
      {children}
    </p>
  );
}

/** Label + control + hint + error. `children` receives the ids to wire aria attributes. */
export function Field({
  id,
  label,
  hint,
  error,
  className,
  children,
}: {
  id: string;
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  className?: string;
  children: (aria: {
    id: string;
    'aria-invalid': boolean | undefined;
    'aria-describedby': string | undefined;
  }) => ReactNode;
}) {
  const describedBy = [hint ? `${id}-hint` : null, error ? `${id}-error` : null]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={className}>
      <Label htmlFor={id}>{label}</Label>
      {children({
        id,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': describedBy || undefined,
      })}
      <FieldHint id={`${id}-hint`}>{hint}</FieldHint>
      <FieldError id={`${id}-error`}>{error}</FieldError>
    </div>
  );
}

/** Form-level error (e.g. server failure) with optional request id. */
export function FormAlert({
  message,
  requestId,
  requestIdLabel,
}: {
  message: ReactNode;
  requestId?: string | undefined;
  requestIdLabel?: (id: string) => string;
}) {
  return (
    <div
      role="alert"
      className="rounded-lg border border-[var(--negative)] bg-[var(--negative-bg)] px-3 py-2 text-sm text-[var(--negative)]"
    >
      <p>{message}</p>
      {requestId ? (
        <p className="mt-0.5 font-mono text-xs">
          {requestIdLabel ? requestIdLabel(requestId) : requestId}
        </p>
      ) : null}
    </div>
  );
}
